import React, { useEffect, useRef, useState } from 'react';

/* Load Plotly from CDN once (no bundler config needed) */
function usePlotly() {
  const [ready, setReady] = useState(typeof window !== 'undefined' && !!window.Plotly);
  useEffect(() => {
    if (ready) return;
    const id = 'plotly-cdn';
    const ex = document.getElementById(id);
    const onload = () => setReady(true);
    if (ex) { ex.addEventListener('load', onload); return () => ex.removeEventListener('load', onload); }
    const s = document.createElement('script');
    s.id = id;
    s.src = 'https://cdn.plot.ly/plotly-2.35.2.min.js';
    s.async = true;
    s.onload = onload;
    document.body.appendChild(s);
    return () => { s.onload = null; };
  }, [ready]);
  return ready;
}

/**
 * Props:
 *   - coin: 'BTC'
 *   - interval: '1h'
 *   - rows: array [{timestamp, open, high, low, close, volume_token|volume_usd}]
 *   - paddingPct: number (default 0.05 => 5% extra space top/bottom)
 */
export default function SimpleCandleAutoFit({ coin='BTC', interval='1h', rows=[], paddingPct=0.06 }) {
  const ready = usePlotly();
  const divRef = useRef(null);
  const guarding = useRef(false); // avoid relayout loops

  useEffect(() => {
    if (!ready || !divRef.current) return;

    const P = window.Plotly;
    const n = (x) => Number(x);
    const t = rows.map(r => new Date(n(r.timestamp) * 1000));
    const open  = rows.map(r => n(r.open));
    const high  = rows.map(r => n(r.high));
    const low   = rows.map(r => n(r.low));
    const close = rows.map(r => n(r.close));
    const vol   = rows.map(r => n(r.volume_token != null ? r.volume_token : (r.volume_usd || 0)));

    const traces = [
      // Row 1: Candles
      {
        type: 'candlestick',
        x: t, open, high, low, close,
        xaxis: 'x', yaxis: 'y',
        name: `${coin} ${interval}`,
        increasing: { line: { color: '#21c48c' } },
        decreasing: { line: { color: '#ef4d4d' } },
      },
      // Row 2: Volume
      {
        type: 'bar',
        x: t, y: vol,
        xaxis: 'x2', yaxis: 'y2',
        name: 'Volume',
        marker: { color: close.map((c, i) => (c >= open[i] ? 'rgba(33,196,140,0.8)' : 'rgba(239,77,77,0.8)')) }
      },
    ];

    const layout = {
      paper_bgcolor: '#0b0f14',
      plot_bgcolor:  '#0b0f14',
      font: { color: '#cbd5e1' },
      showlegend: false,
      dragmode: 'pan',
      hovermode: 'x unified',
      grid: { rows: 2, columns: 1, pattern: 'independent' },
      xaxis:  { matches: 'x', showspikes: true, spikemode: 'across', spikecolor: '#94a3b8', spikethickness: 1 },
      yaxis:  { domain: [0.28, 1.0], gridcolor: 'rgba(255,255,255,0.06)' },
      xaxis2: { matches: 'x', anchor: 'y2', showspikes: true },
      yaxis2: { domain: [0.0, 0.24], gridcolor: 'rgba(255,255,255,0.06)' },
      margin: { l: 56, r: 10, t: 10, b: 24 },
    };

    const config = { responsive: true, displaylogo: false, scrollZoom: true };

    P.newPlot(divRef.current, traces, layout, config).then(() => {
      // Initial y-fit to all data with padding
      if (t.length) {
        const lo = Math.min.apply(null, low);
        const hi = Math.max.apply(null, high);
        const pad = (hi - lo) * paddingPct;
        P.relayout(divRef.current, { 'xaxis.range': [t[0], t[t.length - 1]], 'yaxis.range': [lo - pad, hi + pad] });
      }
    });

    // Helper: recompute y-range for the currently visible x-range
    const fitYForRange = (x0, x1) => {
      let minL = Infinity, maxH = -Infinity, any = false;
      for (let i = 0; i < t.length; i++) {
        const ti = t[i];
        if (ti >= x0 && ti <= x1) {
          any = true;
          if (low[i]  < minL) minL = low[i];
          if (high[i] > maxH) maxH = high[i];
        }
      }
      if (!any || !isFinite(minL) || !isFinite(maxH)) return;
      const pad = (maxH - minL) * paddingPct;
      guarding.current = true;
      P.relayout(divRef.current, { 'yaxis.range': [minL - pad, maxH + pad] }).then(() => { guarding.current = false; });
    };

    // When user pans/zooms horizontally, refit Y with padding
    const onRelayout = (ev) => {
      if (guarding.current) return;
      const x0 = ev['xaxis.range[0]'] || (ev['xaxis.range'] && ev['xaxis.range'][0]);
      const x1 = ev['xaxis.range[1]'] || (ev['xaxis.range'] && ev['xaxis.range'][1]);
      if (x0 && x1) fitYForRange(new Date(x0), new Date(x1));
    };

    divRef.current.addEventListener('plotly_relayout', onRelayout);

    // keep it responsive
    const ro = new ResizeObserver(() => P.Plots.resize(divRef.current));
    ro.observe(divRef.current);

    return () => {
      ro.disconnect();
      divRef.current.removeEventListener('plotly_relayout', onRelayout);
      P.purge(divRef.current);
    };
  }, [ready, coin, interval, rows, paddingPct]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ color: '#cbd5e1', marginBottom: 8, fontWeight: 600 }}>
        {coin} • {interval.toUpperCase()}
      </div>
      {!ready && <div style={{ color:'#94a3b8' }}>Loading chart…</div>}
      <div ref={divRef} style={{ width: '100%', height: 560 }} />
    </div>
  );
}
